import { useCallback, useEffect, useMemo } from 'react';
import type React from 'react';
import { ContactLogic } from '@/modules/contact';
import type { ContactList } from '@/modules/contact';
import type { ContactAction } from '@/modules/contact/reducer';
import type { Contact } from '@/modules/contact';
import type { MessageIntent } from '@/modules/template';
import type { TopicAwarenessMap } from '@/services/meprfo';
import { ShareWizardLogic } from './logic';

export interface UseShareRecipientsParams {
  /** The live recipient selection, from ContactStateContext. */
  recipients: ContactList;
  /** Who has already been sent to in this session, from the stored session. */
  confirmedContactIds: Set<string>;
  /** Who already knows about this topic, from `useTopicAwareness`. */
  awarenessMap: TopicAwarenessMap;
  isUpdateFlow: boolean;
  /** True once this sender has spent the topic type's intro allowance. */
  introLimitReached: boolean;
  /** The sender's "don't tell people who already know" choice (#581). */
  excludeAwareContacts: boolean;
  /** The raw "Share as" choice, from `useShareFormChoices`. */
  singletonMode: boolean;
  dispatch: React.Dispatch<ContactAction>;
  presentToast: (options: {
    message: string;
    duration: number;
    color: string;
  }) => void;
}

export interface ShareRecipients {
  /** Who gets which message. */
  contactsByIntent: Record<MessageIntent, Contact[]>;
  /** Contacts an intro cannot reach, and why, for the recipient list's badges. */
  introBlockedKeys: ReturnType<
    typeof ShareWizardLogic.routeRecipients
  >['introBlockedKeys'];
  /** The intent each contact is routed to, for the same badges. */
  contactIntents: ReturnType<
    typeof ShareWizardLogic.routeRecipients
  >['contactIntents'];
  /** Label for the group-text opt-in, or null when it should be hidden. */
  /** Recipients per intent, in compose-step order. */
  recipientCountsByIntent: readonly number[];
  /** What actually sizes a leg — see the note at its definition. */
  effectiveSingletonMode: boolean;
  intentReachesOnePerson: (intent: MessageIntent) => boolean;
  getContactsForIntent: (intent: MessageIntent) => Contact[];
}

/**
 * Who gets which of this flow's messages, and how many people one message
 * carries.
 *
 * Routing is the wizard's central rule and the input to almost everything
 * downstream — the visible steps, the send plan, the previews, and the badges
 * the recipient list draws — so it is resolved once here from the recipient
 * selection, what the session already sent, and what the server says each
 * person already knows.
 */
export function useShareRecipients({
  recipients,
  confirmedContactIds,
  awarenessMap,
  isUpdateFlow,
  introLimitReached,
  excludeAwareContacts,
  singletonMode,
  dispatch,
  presentToast,
}: UseShareRecipientsParams): ShareRecipients {
  const { contactsByIntent, introBlockedKeys, contactIntents } = useMemo(
    () =>
      ShareWizardLogic.routeRecipients({
        recipients,
        confirmedContactIds,
        awareness: awarenessMap,
        isUpdateFlow,
        introLimitReached,
        excludeAwareContacts,
      }),
    [
      isUpdateFlow,
      awarenessMap,
      recipients,
      introLimitReached,
      confirmedContactIds,
      excludeAwareContacts,
    ]
  );

  // Awareness can resolve after a contact is picked (#581): the recipient list
  // asks a screenful at a time, and a name typed or tapped in before that answer
  // lands may turn out to have already seen the topic. While the exclude box is
  // checked such a pick can never be sent to, so drop it and say why — leaving
  // it in the box would read as a recipient the wizard silently ignores.
  useEffect(() => {
    if (!excludeAwareContacts) return;
    const nowExcluded = recipients.filter(contact => {
      if (confirmedContactIds.has(contact.localId)) return false;
      const userId = contact.snowballId;
      return !!userId && !!awarenessMap.get(userId)?.introducedByAnyone;
    });
    if (nowExcluded.length === 0) return;
    for (const contact of nowExcluded) {
      dispatch({
        type: 'REMOVE_RECIPIENT',
        payload: { localId: contact.localId },
      });
    }
    presentToast({
      message: ShareWizardLogic.alreadyIntroducedNotice(
        nowExcluded.map(
          contact => ContactLogic.informalName(contact) || 'A contact'
        )
      ),
      duration: 4000,
      color: 'warning',
    });
  }, [
    excludeAwareContacts,
    recipients,
    awarenessMap,
    confirmedContactIds,
    dispatch,
    presentToast,
  ]);

  const getContactsForIntent = useCallback(
    (intent: MessageIntent): Contact[] => contactsByIntent[intent],
    [contactsByIntent]
  );

  const recipientCountsByIntent = useMemo(
    () => ShareWizardLogic.recipientCountsByIntent(contactsByIntent),
    [contactsByIntent]
  );

  // Whether a group text is even possible: false when no intent has more than
  // one recipient, so grouping is moot. The "Share as" segment answers this for
  // itself from the counts; here it decides the effective mode below.
  const canGroupText = ShareWizardLogic.canGroupText(recipientCountsByIntent);

  // Effective singleton mode. When there is no group-texts choice — i.e. no
  // intent has more than one recipient — every message goes to exactly one
  // person, so singleton semantics apply regardless of the raw flag: legs
  // hold a single recipient and templates may offer name-bearing greetings
  // (the singleton-mode options that embed `{_recipientInformalName}`). The raw
  // `singletonMode` flag only drives the user-facing "Share as" control.
  const effectiveSingletonMode = singletonMode || !canGroupText;

  // Whether every message for ONE intent reaches exactly one person: true in
  // singleton mode, and also when that intent has a lone recipient (a "group
  // text" to one person is still one person), even while another intent has
  // several and keeps the group-text choice on.
  //
  // Distinct from `effectiveSingletonMode`, which answers the whole wizard —
  // it sizes every leg in the send plan and backs the "Share as" control, so
  // it cannot narrow to one intent. This is the per-intent reading derived from
  // it, and it decides which options a field offers: the compose step, the
  // field walk, completeness and the stale-value prune all have to agree on it,
  // so it is defined here once and handed out.
  const intentReachesOnePerson = useCallback(
    (intent: MessageIntent): boolean =>
      effectiveSingletonMode || (contactsByIntent[intent]?.length ?? 0) <= 1,
    [effectiveSingletonMode, contactsByIntent]
  );

  return {
    contactsByIntent,
    introBlockedKeys,
    contactIntents,
    recipientCountsByIntent,
    effectiveSingletonMode,
    intentReachesOnePerson,
    getContactsForIntent,
  };
}
